import type { ChatMessage, ChatTranscript, ChatRole } from '../schemas/chat';
import {
  chatAttachmentKindKey,
  chatAttachmentTranslations,
} from '../i18n/chat-attachment';
import { t } from '../i18n/types';
import {
  applyDisplayEdits,
  hasHesitation,
  hesitationEdits,
  type DisplayEdit,
} from '../filler-words';

export type ChatDiarizationSegment = {
  speaker: string;
  /**
   * Роль говорящего в машинном виде. `speaker` — человекочитаемая строка
   * («agent (Марина)»), и разбирать её обратно значит зависеть от формата
   * рендера. Порог значимости чата решает по этому полю.
   */
  role?: ChatRole;
  start: number;
  end: number;
  text: string;
  confidence?: number;
  /**
   * `true` — у ОДНОГО сегмента чата, первого после первого ответа сотрудника
   * (`role: 'agent'`), когда сам этот ответ сегментом не стал: пустой после
   * очистки разметки (голый файл `[DISK FILE …]`). Без признака граница «до
   * первого ответа сотрудника» (#1578) сдвинулась бы на его следующее
   * сообщение. Первый ответ остался сегментом — признака нет нигде: границу
   * видно по роли. Нет поля ни у одного сегмента — ещё и сегменты, построенные
   * до #1578.
   */
  afterAgentMessage?: true;
  /**
   * `true` — у одного сегмента чата, первого после сообщения автора
   * НЕИЗВЕСТНОЙ роли, которое стоит до первого ответа сотрудника и сегментом
   * не стало (голый файл, одна разметка). Оно могло быть этим ответом, поэтому
   * границу первой минуты чата шаг не пишет вовсе (#1578). Отдельное поле, а
   * не значение `afterAgentMessage`: код до этой правки ждёт там `true` и
   * отверг бы другое значение, а незнакомое поле просто отбрасывает.
   */
  afterUnknownMessage?: true;
};

/**
 * Маркер вложения — не разметка Bitrix, а НАША вставка (`selectOpenLineMessages`),
 * добавленная уже после API именно затем, чтобы вложение дожило до транскрипта.
 *
 * Он обязан пережить санитайзер, и защищать его lookahead'ом внутри общего
 * правила `\[\/?[a-z][^\]]*\]` недостаточно: незакрытая `[` где-то раньше в
 * подписи («Розмір [size 42 і більше [attachment: image]») уводит `[^\]]*`
 * мимо маркера к его же закрывающей скобке, и правило съедает подпись вместе
 * с маркером — ровно то стирание, ради отмены которого маркер и существует.
 * Поэтому строка разбивается ПО маркерам, и стрипается только то, что между.
 */
const ATTACHMENT_MARKER_SPLIT = /(\[attachment: [^\]]*\])/i;
/**
 * Та же грамматика для «убрать все маркеры из строки»: `split` работает без
 * `g`, а `replace` без него снял бы только первый. Регулярка выводится из
 * первой, а не пишется заново, — иначе расширить одну и забыть вторую значит
 * развести защиту маркера с его вырезанием, и чат из одних картинок снова
 * стал бы «разговором».
 */
const ATTACHMENT_MARKER_ALL = new RegExp(ATTACHMENT_MARKER_SPLIT.source, 'gi');

/**
 * Тот же маркер, разобранный на части: вид вложения и, если файлов было
 * несколько, их число. Выводится из той же грамматики, что и две регулярки
 * выше, и по той же причине: разойтись им нельзя.
 */
const ATTACHMENT_MARKER_PARSE = /\[attachment: ([^\]×]+?)(?: ×(\d+))?\]/gi;

/**
 * Маркер цитаты (#889) — тоже НАША вставка (`selectOpenLineMessages`), а не
 * разметка Bitrix: `[quote]Имя: текст цитаты[/quote]`, затем свой текст
 * отправителя. Цитата остаётся внутри текста реплики — её видят PII-редакция,
 * анализ, экспорт и CRM, — но перестаёт быть словами отправителя: счёт реплик её
 * вычитает, вывод подписывает. Имя автора — обычный текст внутри маркера, а не
 * атрибут: редактор PII вправе его заменить, не ломая грамматику.
 */
const QUOTE_OPEN = '[quote]';
const QUOTE_CLOSE = '[/quote]';
/** Оба маркера цитаты по отдельности — для атомарных правок показа. */
const QUOTE_MARKER_PARSE = /\[\/?quote\]/gi;
/**
 * Цитата целиком — для «своей речи» реплики. Незакрытая — до конца текста.
 *
 * Текст, первый маркер которого закрывает цитату, начинается внутри неё —
 * всё до `[/quote]` чужие слова. Поддельный маркер обезврежен на входе
 * (`disarmForgedMarker`), так что такой текст — наш маркер, потерявший
 * открывающую половину: её съела PII-редакция («адрес: [quote]Олег: Киев…»
 * → «адрес [ADDRESS]…[/quote]») или лимит транскрипта ассистента оставил
 * хвост сообщения, отрезанный внутри цитаты (#1120).
 *
 * Такая цитата начинается там, где кончилась предыдущая область (или с начала
 * текста), — не только с нулевого смещения: у второй из двух цитат одного
 * сообщения открывающую половину съедает та же редакция (#2127). Старт
 * привязан к концу `[/quote]`, а не к любой позиции: иначе каждая позиция
 * текста без маркеров сканировала бы его до конца.
 */
const QUOTE_REGION_ALL =
  /\[quote\][\s\S]*?(?:\[\/quote\]|$)|(?:^|(?<=\[\/quote\]))(?:(?!\[quote\])[\s\S])*?\[\/quote\]/gi;

/**
 * Где в сохранённом тексте реплики стоят цитаты — вместе с маркерами. Экран
 * показывает их приглушённо (#889): чужие слова отделены от своих и глазом.
 */
export function quotedRegions(text: string): Array<{ start: number; end: number }> {
  return [...text.matchAll(QUOTE_REGION_ALL)].map((match) => ({
    start: match.index ?? 0,
    end: (match.index ?? 0) + match[0].length,
  }));
}

/**
 * Текст реплики без цитат — только то, что сказал сам отправитель (#889).
 * По нему решают, КТО произнёс слова: цитата клиента в ответе оператора не
 * делает эти слова оператора.
 */
export function withoutQuotedText(text: string): string {
  return text.replace(QUOTE_REGION_ALL, ' ');
}

/**
 * Есть ли в тексте цитата — быстрый отсев для `withoutQuotedText`. Закрывающий
 * маркер тоже: без открывающего цитата начинается с конца предыдущей области
 * (или с начала текста), см. `QUOTE_REGION_ALL`.
 */
export function hasQuotedText(text: string): boolean {
  return /\[\/?quote\]/i.test(text);
}
/**
 * Текст с маркерами цитаты, заглушёнными пробелами той же длины: смещения не
 * сдвигаются, а маркер перестаёт быть символами, которые заземление могло бы
 * принять за край значения (#2114). Грамматика маркера — та же
 * `QUOTE_MARKER_PARSE`, второй копии нет.
 */
export function blankQuoteMarkers(text: string): string {
  if (!text.includes('[')) return text;
  return text.replace(QUOTE_MARKER_PARSE, (marker) => ' '.repeat(marker.length));
}

/**
 * Все наши маркеры одним разбиением: санитайзер стрипает только то, что МЕЖДУ
 * ними (см. `ATTACHMENT_MARKER_SPLIT`, почему не lookahead).
 */
const CHAT_MARKER_SPLIT = /(\[attachment: [^\]]*\]|\[\/?quote\])/i;

export interface TranscriptDisplayOptions {
  /**
   * Скрыть звуки колебания («е-е-е», «ммм») — только для звонков: в чате это
   * текст, который человек написал сам (#819).
   */
  stripHesitations?: boolean;
}

/** Маркеры вложений как атомарные правки показа. */
function attachmentMarkerEdits(sourceText: string, language: string): DisplayEdit[] {
  if (!sourceText.includes('[')) return [];
  const edits: DisplayEdit[] = [];
  for (const match of sourceText.matchAll(ATTACHMENT_MARKER_PARSE)) {
    const start = match.index ?? 0;
    const label = t(
      language,
      chatAttachmentTranslations,
      chatAttachmentKindKey(match[1] ?? '')
    );
    edits.push({
      start,
      end: start + match[0].length,
      shown: match[2] ? `[${label} ×${match[2]}]` : `[${label}]`,
      atomic: true,
    });
  }
  return edits;
}

/**
 * Маркеры цитаты как атомарные правки показа: `[quote]` → `[Цитата] «`,
 * `[/quote]` → `»`. Экран, копирование, экспорт и CRM получают один вид, а
 * выделение не разрезает маркер пополам.
 */
function quoteMarkerEdits(sourceText: string, language: string): DisplayEdit[] {
  if (!sourceText.includes('[')) return [];
  const label = t(language, chatAttachmentTranslations, 'quote');
  const edits: DisplayEdit[] = [];
  for (const match of sourceText.matchAll(QUOTE_MARKER_PARSE)) {
    const start = match.index ?? 0;
    const closing = match[0].startsWith('[/');
    edits.push({
      start,
      end: start + match[0].length,
      shown: closing ? '»' : `[${label}] «`,
      atomic: true,
    });
  }
  return edits;
}

/**
 * Все правки, которыми показанный транскрипт отличается от сохранённого, — в
 * координатах сохранённого. Один источник и для отрисовки, и для перевода
 * позиций выделения и подсветки: разойтись им нельзя.
 */
export function transcriptDisplayEdits(
  sourceText: string,
  language: string,
  options: TranscriptDisplayOptions = {}
): DisplayEdit[] {
  const markers = [
    ...attachmentMarkerEdits(sourceText, language),
    ...quoteMarkerEdits(sourceText, language),
  ].sort((a, b) => a.start - b.start);
  if (!options.stripHesitations) return markers;
  const sounds = hesitationEdits(sourceText).filter(
    (sound) =>
      !markers.some((marker) => sound.start < marker.end && sound.end > marker.start)
  );
  return [...markers, ...sounds].sort((a, b) => a.start - b.start);
}

/**
 * Скрывает ли показ звуки колебания — одно решение на все поверхности.
 *
 * Панель, копирование, выгрузка, поле CRM, комментарий и расшифровка звонка
 * показывают один и тот же транскрипт, и «звонок — да, переписка — нет»
 * решается здесь, а не шестью копиями условия по репозиторию (ревью #827).
 */
export function transcriptDisplayOptions(
  channel: string | undefined
): TranscriptDisplayOptions {
  return { stripHesitations: hidesHesitationSounds(channel) };
}

/** То же решение отдельным предикатом — для флага в пропсах компонента. */
export function hidesHesitationSounds(channel: string | undefined): boolean {
  return channel === 'call';
}

/** Текст реплики так, как его видит человек. */
export function renderTranscriptText(
  sourceText: string,
  language: string,
  options: TranscriptDisplayOptions = {}
): string {
  return applyDisplayEdits(
    sourceText,
    transcriptDisplayEdits(sourceText, language, options)
  );
}

/**
 * Многострочный транскрипт «Имя: реплика» так, как его видит человек.
 *
 * Правки считаются по тексту КАЖДОЙ реплики, а метка говорящего остаётся как
 * есть. Иначе звук в конце реплики уходил бы вместе с пробелом перед ним и
 * из «Олена: Ммм.» получалось «Олена:.», а реплика из одних звуков, которую
 * положено оставить, теряла бы звук: буквы метки сошли бы за содержание.
 *
 * Меткой считается только префикс до двоеточия, в котором нет звука. Текст
 * звонка без диаризации меток не несёт, а двоеточие в нём обычное: в
 * «Е-е-е, номер заказа: 123» и в «Ммм: так, 100 гривень» начало — реплика, и
 * фильтруется вся строка. Вопрос «есть ли звук» задаётся тексту напрямую
 * (`hasHesitation`), а не через правки показа: на префиксе из одних звуков
 * правок нет — реплику из «Ммм.» мы не опустошаем, — и звук сошёл бы за метку.
 */
export function renderTranscriptDocument(
  sourceText: string,
  language: string,
  options: TranscriptDisplayOptions = {}
): string {
  return sourceText
    .split('\n')
    .map((line) => {
      const match = /^([^:\n]{1,60}:\s*)([\s\S]*)$/u.exec(line);
      const labelled =
        match && !(options.stripHesitations && hasHesitation(match[1] ?? ''))
          ? match
          : null;
      return labelled
        ? `${labelled[1]}${renderTranscriptText(labelled[2] ?? '', language, options)}`
        : renderTranscriptText(line, language, options);
    })
    .join('\n');
}

/**
 * Точка показа → точка сохранённого текста.
 *
 * Внутри атомарной правки сторона решает, к какому краю прижаться. В месте
 * скрытого звука начало выделения встаёт после него, а конец — перед ним: звук
 * не попадает в аннотацию, которую пользователь поставил на соседние слова.
 */
function displayToSource(
  edits: readonly DisplayEdit[],
  sourceLength: number,
  displayOffset: number,
  side: 'start' | 'end'
): number {
  let display = 0;
  let source = 0;
  for (const edit of edits) {
    const gap = edit.start - source;
    if (displayOffset < display + gap) return source + (displayOffset - display);
    if (displayOffset === display + gap) {
      if (edit.shown === '') return side === 'start' ? edit.end : edit.start;
      return edit.start;
    }
    display += gap;
    source = edit.start;
    if (displayOffset < display + edit.shown.length) {
      if (!edit.atomic) return source + (displayOffset - display);
      return side === 'start' ? edit.start : edit.end;
    }
    display += edit.shown.length;
    source = edit.end;
  }
  return Math.min(source + (displayOffset - display), sourceLength);
}

/**
 * Точка сохранённого текста → точка показа по УЖЕ посчитанным правкам.
 *
 * Экспортирована ради того, кто переводит много границ одной реплики: у
 * `toDisplayPoint` внутри полный разбор текста, и на k подсветок он повторялся
 * бы k раз поверх того, что уже сделала отрисовка (ревью #827).
 */
export function displayPointFromEdits(
  edits: readonly DisplayEdit[],
  sourceOffset: number,
  side: 'start' | 'end'
): number {
  let display = 0;
  let source = 0;
  for (const edit of edits) {
    if (sourceOffset <= edit.start) return display + (sourceOffset - source);
    display += edit.start - source;
    source = edit.start;
    if (sourceOffset < edit.end) {
      if (!edit.atomic && edit.shown.length === edit.end - edit.start) {
        return display + (sourceOffset - edit.start);
      }
      return side === 'start' ? display : display + edit.shown.length;
    }
    display += edit.shown.length;
    source = edit.end;
  }
  return display + (sourceOffset - source);
}

/**
 * Точка выделения в координатах ИСХОДНОГО текста.
 *
 * Локализованная подпись длиннее или короче машинного токена, а скрытый звук
 * колебания не показывается вовсе, поэтому выделение мышью даёт смещение в
 * координатах экрана, а аннотация обязана храниться в координатах сохранённого
 * текста — иначе при следующем открытии подсветка ляжет на другие символы.
 *
 * Маркер атомарен, отдельных позиций внутри него в исходном тексте нет, поэтому
 * сторона решает, к какому его краю прижаться: начало выделения — к началу
 * маркера, конец — к концу. Так любая часть подписи выделяет маркер целиком, а
 * выделение, целиком лежащее внутри подписи, не схлопывается в ноль символов.
 *
 * @param side какой конец выделения переводится
 */
export function toSourcePoint(
  sourceText: string,
  language: string,
  displayOffset: number,
  side: 'start' | 'end',
  options: TranscriptDisplayOptions = {}
): number {
  // Без раннего выхода на нуле: реплика может начинаться со скрытого звука, и
  // выделение с первой показанной буквы начинается ПОСЛЕ него.
  return displayToSource(
    transcriptDisplayEdits(sourceText, language, options),
    sourceText.length,
    displayOffset,
    side
  );
}

/**
 * Точка сохранённого текста в координатах ПОКАЗА — для подсветки аннотации,
 * которая хранится в исходных координатах, а рисуется поверх показанного.
 */
export function toDisplayPoint(
  sourceText: string,
  language: string,
  sourceOffset: number,
  side: 'start' | 'end',
  options: TranscriptDisplayOptions = {}
): number {
  return displayPointFromEdits(
    transcriptDisplayEdits(sourceText, language, options),
    sourceOffset,
    side
  );
}

/**
 * Границы маркеров в исходном тексте — по той же грамматике, что и вывод.
 */
function attachmentMarkerSpans(
  sourceText: string
): Array<{ start: number; end: number }> {
  const spans: Array<{ start: number; end: number }> = [];
  for (const parse of [ATTACHMENT_MARKER_PARSE, QUOTE_MARKER_PARSE]) {
    for (const match of sourceText.matchAll(parse)) {
      const start = match.index ?? 0;
      spans.push({ start, end: start + match[0].length });
    }
  }
  return spans;
}

/**
 * Раздвинуть диапазон до целых маркеров.
 *
 * Маркер атомарен: «фото» из «[вложение: фото]» — не слово в реплике, а часть
 * нашей пометки. Граница, упавшая внутрь него, растягивается до его края —
 * иначе получаются две поломки сразу: аннотация нулевой ширины (если выделены
 * только буквы подписи — сохраняется, но никогда не подсвечивается) и разрез
 * маркера пополам при отрисовке, из-за которого наружу лезут куски машинного
 * токена.
 */
export function expandRangeOverMarkers(
  sourceText: string,
  start: number,
  end: number
): { start: number; end: number } {
  let expandedStart = start;
  let expandedEnd = end;

  for (const span of attachmentMarkerSpans(sourceText)) {
    if (expandedStart > span.start && expandedStart < span.end)
      expandedStart = span.start;
    if (expandedEnd > span.start && expandedEnd < span.end) expandedEnd = span.end;
  }

  return { start: expandedStart, end: expandedEnd };
}

/**
 * Человекочитаемый вид маркеров вложения — для транскрипта на экране и в CRM.
 *
 * Машинная форма остаётся в самом тексте: её читает LLM, по ней
 * `evaluateChatSignificance` отличает реплику из одних вложений от разговора,
 * и она же не зависит от языка проекта. Локализация — исключительно вывод, и
 * потому живёт здесь, рядом с грамматикой маркера, а не в каждой поверхности
 * своей копией регулярки (#476).
 *
 * Вид вложения приходит от вендора (`files[id].type`); незнакомый вид
 * становится родовым «файлом» — тем же запасным вариантом, что и у самого
 * маркера.
 *
 * @param text текст сегмента или всего транскрипта
 * @param language язык поверхности (uk/ru/en/es); прочее уходит в en
 */
export function localizeAttachmentMarkers(text: string, language: string): string {
  if (!text.includes('[')) return text;

  const quoteLabel = t(language, chatAttachmentTranslations, 'quote');
  return text
    .replace(ATTACHMENT_MARKER_PARSE, (_match, kind: string, count?: string) => {
      const label = t(language, chatAttachmentTranslations, chatAttachmentKindKey(kind));
      return count ? `[${label} ×${count}]` : `[${label}]`;
    })
    .replace(QUOTE_MARKER_PARSE, (marker) =>
      marker.startsWith('[/') ? '»' : `[${quoteLabel}] «`
    );
}

function stripChatMarkup(value: string): string {
  return (
    value
      .split(CHAT_MARKER_SPLIT)
      // Снятая разметка может сложить маркер из текста — `[[b][/b]quote]`
      // даёт `[quote]`. Обезвреживается уже очищенный текст (#889).
      .map((part, index) =>
        index % 2 === 1 ? part : disarmForgedMarker(stripBbcode(part))
      )
      .join('')
  );
}

/**
 * Strip Bitrix24 / BBCode markup that leaks into open-line chat messages.
 *
 * Bitrix24 wraps links, mentions, files and formatting in BBCode-style tags
 * (`[URL]…[/URL]`, `[B]…[/B]`, `[DISK FILE ID=…]`) and inserts `------` divider
 * lines. Without stripping, that raw markup ends up both in the LLM analysis
 * input and in the rendered transcript segments. We keep the human-readable
 * payload (link target, mention name, bold text) and drop the tags themselves.
 *
 * ASCII-only tag matching (`[a-z]`) deliberately leaves Cyrillic bracketed text
 * like `[важно]` untouched — only BBCode-shaped tokens are removed.
 */
function stripBbcode(value: string): string {
  return (
    value
      // [BR] line break -> newline (collapsed to a space by normalizeText)
      .replace(/\[br\]/gi, '\n')
      // Labeled link: [URL=https://x]label[/URL] -> label
      .replace(/\[url=[^\]]*\]([\s\S]*?)\[\/url\]/gi, '$1')
      // Bare link: [URL]https://x[/URL] -> https://x
      .replace(/\[url\]([\s\S]*?)\[\/url\]/gi, '$1')
      // Mention: [USER=123]Name[/USER] -> Name
      .replace(/\[user=[^\]]*\]([\s\S]*?)\[\/user\]/gi, '$1')
      // Image markup carries no readable text -> drop
      .replace(/\[img\][\s\S]*?\[\/img\]/gi, '')
      // Bitrix action / file placeholders -> drop entirely
      .replace(/\[(?:disk|send|put|copy|call|chat|icon)[^\]]*\]/gi, '')
      // Any remaining BBCode tag (open or close) -> drop, keep inner text
      .replace(/\[\/?[a-z][^\]]*\]/gi, '')
      // Divider lines made of repeated -, =, _ or * -> drop
      .replace(/^[ \t]*[-=_*]{3,}[ \t]*$/gm, '')
  );
}

function normalizeText(value: string): string {
  return stripChatMarkup(value).replace(/\s+/g, ' ').trim();
}

function resolveRole(message: ChatMessage): ChatRole | 'participant' {
  return message.role || message.author?.role || 'participant';
}

function resolveSpeaker(message: ChatMessage): string {
  const role = resolveRole(message);
  const name = message.author?.name || message.author?.id;
  return name ? `${role} (${name})` : role;
}

function parseTimestampSeconds(value?: string): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  if (Number.isNaN(parsed)) return null;
  return Math.floor(parsed / 1000);
}

export function buildChatTranscriptText(transcript: ChatTranscript): string {
  const directText = transcript.text ? transcript.text.trim() : '';
  if (directText.length > 0) {
    return directText;
  }

  if (!transcript.messages || transcript.messages.length === 0) {
    return '';
  }

  return transcript.messages
    .map((message) => {
      const text = normalizeText(message.text);
      if (!text) {
        return '';
      }
      const timestamp = message.timestamp ? `[${message.timestamp}] ` : '';
      const speaker = resolveSpeaker(message);
      return `${timestamp}${speaker}: ${text}`;
    })
    .filter((line) => line.length > 0)
    .join('\n');
}

/**
 * Есть ли в тексте строка, которую `buildChatTranscriptText` подписала бы этой
 * ролью: `[метка времени] роль (имя): текст` или `роль: текст`. Формат тот же,
 * что у `resolveSpeaker` и сборки строки выше, а не вторая копия правила
 * «кто автор»: текст чата, пришедший не из сборки (публичный `POST /records`
 * несёт произвольный `textContent`), подписей не имеет вовсе.
 *
 * Это проверка ФОРМЫ строки, а не подлинности: такой же вид может набрать и
 * автор сообщения. Она отсекает неподписанный текст, не подделку.
 */
export function chatTranscriptHasRoleLine(text: string, role: ChatRole): boolean {
  const line = new RegExp(`^(?:\\[[^\\]\\n]*\\] )?${role}(?: \\([^\\n]*\\))?: \\S`);
  return text.split('\n').some((candidate) => line.test(candidate));
}

export function buildChatDiarizationSegments(
  transcript: ChatTranscript
): ChatDiarizationSegment[] {
  const messages = transcript.messages || [];
  if (messages.length === 0) {
    const text = transcript.text ? transcript.text.trim() : '';
    if (!text) {
      return [];
    }
    return [
      {
        speaker: 'chat',
        start: 0,
        end: 1,
        text,
      },
    ];
  }

  const messageTimes = messages.map((message) =>
    parseTimestampSeconds(message.timestamp)
  );
  const baseTime = messageTimes.find((value) => value !== null);
  const texts = messages.map((message) => normalizeText(message.text));
  // Первый ответ сотрудника в исходном порядке — ДО фильтра пустых ниже. Стал
  // он пустым (голый файл) — сегмента у него не будет, и границу «до первого
  // ответа сотрудника» (#1578) несёт первый сегмент после него
  // (`afterAgentMessage`). Остался сегментом — граница видна по его роли.
  const firstAgentIndex = messages.findIndex(
    (message) => resolveRole(message) === 'agent'
  );
  const firstKeptAfter = (from: number) =>
    from < 0 ? -1 : texts.findIndex((text, index) => index > from && text.length > 0);
  const markIndex =
    firstAgentIndex >= 0 && texts[firstAgentIndex] === ''
      ? firstKeptAfter(firstAgentIndex)
      : -1;
  // Пустое сообщение автора неизвестной роли ДО первого ответа сотрудника тоже
  // не оставляет сегмента — а могло быть этим ответом. Первый сегмент после
  // него несёт `afterUnknownMessage`: граница первой минуты чата не известна.
  const unknownDroppedIndex = messages.findIndex((message, index) => {
    if (firstAgentIndex >= 0 && index >= firstAgentIndex) return false;
    const role = resolveRole(message);
    return texts[index] === '' && role !== 'customer' && role !== 'system';
  });
  const unknownMarkIndex = firstKeptAfter(unknownDroppedIndex);

  return (
    messages
      .map((message, index): ChatDiarizationSegment => {
        const text = texts[index]!;
        const speaker = resolveSpeaker(message);
        const role = resolveRole(message);
        let start = index;

        if (baseTime !== undefined && baseTime !== null) {
          const timestamp = messageTimes[index];
          if (timestamp !== null && timestamp !== undefined) {
            start = Math.max(0, timestamp - baseTime);
          }
        }

        return {
          speaker,
          ...(role !== 'participant' ? { role } : {}),
          start,
          end: start + 1,
          text,
          ...(index === markIndex ? { afterAgentMessage: true as const } : {}),
          ...(index === unknownMarkIndex ? { afterUnknownMessage: true as const } : {}),
        };
      })
      // Drop segments that became empty after markup stripping (e.g. messages
      // that were only `------` dividers or bare file placeholders) so the
      // transcript viewer doesn't render blank bubbles.
      .filter((segment) => segment.text.length > 0)
  );
}

/**
 * Участник чата из словаря `users` ответа истории — ровно те поля, по которым
 * видно СТОРОНУ. Остальные (имя, аватар, отделы, статус) не нужны и не
 * объявляются: это PII, и тянуть её в тип значит приглашать её в логи.
 */
export interface OpenLineUser {
  /** `true` у пользователя, заведённого коннектором открытой линии, — это клиент. */
  connector?: boolean;
  /** `'imconnector'` у клиента; у сотрудника — например `'socservices'`. */
  externalAuthId?: string;
}

/**
 * Клиент ли это — по признакам самого пользователя, а не по его сообщениям.
 *
 * Замер на живом портале (2026-08-29, две сессии): у клиента одновременно
 * `connector: true`, `extranet: true`, `externalAuthId: 'imconnector'`,
 * `type: 'extranet'`; у сотрудника все три первых — false/`socservices`.
 * Достаточно любого из двух проверяемых здесь, и оба наблюдались вместе.
 *
 * `extranet` НЕ проверяется намеренно: экстранет-пользователь бывает и
 * настоящим сотрудником-партнёром, то есть признак шире стороны.
 *
 * `params.connectorMid` у сообщения тоже помечает пришедшее через коннектор,
 * но помечает НЕ ТОЛЬКО клиентское: в сессии 42866 его несли два системных
 * сообщения. Признак пользователя надёжнее признака сообщения.
 */
function isConnectorSide(user: OpenLineUser): boolean {
  return user.connector === true || user.externalAuthId === 'imconnector';
}

/**
 * id клиента открытой линии — по стороне, из словаря `users` ответа истории.
 *
 * Прежде клиентом записывали `activity.AUTHOR_ID`, а у активности открытой
 * линии это сотрудник: на стейджинге во всех 37 чатах после 29.08 «клиентом»
 * был id сотрудника, в 28 — тот же, что у оператора (#889). Нет словаря или
 * клиента в нём — `null`: неизвестного клиента не записываем вовсе.
 */
export function openLineCustomerId(
  users: Record<string, OpenLineUser> | null | undefined
): string | null {
  if (!users) return null;
  // Ответ REST не типизирован: запись словаря бывает `null`.
  const entry = Object.entries(users).find(
    ([, user]) => user != null && isConnectorSide(user)
  );
  return entry ? entry[0] : null;
}

/**
 * Участники открытой линии для `participants` записи: клиент по стороне
 * (`openLineCustomerId`), оператор — ответственный активности. Одна реализация
 * для трёх путей приёма (вебхук, шаг ожидания, импорт CRM), чтобы одна и та же
 * переписка не получала разных участников в зависимости от пути.
 */
export function openLineParticipants(
  users: Record<string, OpenLineUser> | null | undefined,
  operatorId: string | null
): Array<{ id: string; role: 'customer' | 'agent' }> {
  const customerId = openLineCustomerId(users);
  return [
    ...(customerId ? [{ id: customerId, role: 'customer' as const }] : []),
    ...(operatorId ? [{ id: operatorId, role: 'agent' as const }] : []),
  ];
}

/**
 * Роль автора сообщения открытой линии — или `undefined`, когда роли не знаем.
 *
 * **Сторона, а не личность.** Кто именно из сотрудников ответил, роли не
 * решает: перевод чата в другую очередь, подключившийся второй оператор и
 * ответ руководителя — всё это «наша сторона». Сторона приходит в том же
 * ответе `imopenlines.session.history.get`, что и сообщения, — словарём
 * `users`, где клиент помечен как заведённый коннектором (#422).
 *
 * До этого роль решало равенство `senderId === RESPONSIBLE_ID`, и реплики
 * любого другого сотрудника уезжали в промпт клиентскими. На проде это 5
 * реплик в 3 чатах; отсечения живого диалога по этой причине замер не нашёл
 * ни одного, поэтому правка чинит вход анализа, а не отбор.
 *
 * **Отправителя нет в словаре — работает прежнее правило.** Ответ REST не
 * типизирован, и портал вправе словарь не прислать; «не знаю» обязано означать
 * «как раньше», а не «все клиенты» — иначе каждый чат разом станет «оператор
 * не ответил».
 *
 * **Без оператора и без словаря роли не проставляются ВООБЩЕ.** Не «часть» —
 * иначе `rolesKnown` снова станет `true` (он смотрит на наличие поля у любого
 * сегмента) и правило «оператор не ответил» вернётся уже по другой причине:
 * не потому, что оператор молчал, а потому что мы не знали, кто оператор.
 * Отсечение необратимо — снять `is_short_call` с чата нечем, реанализ
 * отказывает. Чат тогда судится только по числу сообщений, ровно как чат из
 * внешнего источника, приславшего сегменты без ролей.
 */
export function resolveOpenLineMessageRole(
  senderId: string,
  operatorId: string | null,
  /**
   * ОБЯЗАТЕЛЬНЫЙ, хотя и допускает `undefined`. Необязательным он был ровно
   * один круг ревью — и этого хватило, чтобы третий продовый путь (импорт из
   * CRM и бэкфилл, `activity-processor.ts`) молча остался на старом правиле, а
   * одна и та же переписка получала разный анализ в зависимости от того, каким
   * путём приехала. У этой функции три call-site, и «три копии, одну забыли» —
   * её собственная история. Требование аргумента делает сторожем компилятор:
   * новый путь обязан назвать словарь или явно сказать, что его нет.
   */
  users: Record<string, OpenLineUser> | null | undefined
): ChatRole | undefined {
  const sender = users?.[senderId];
  if (sender) return isConnectorSide(sender) ? 'customer' : 'agent';

  // Системные врезки платформы автором не являются и в `users` не попадают,
  // поэтому их роль решается отдельно — но только когда словарь ДЕЙСТВИТЕЛЬНО
  // описывает участников. Пустой `{}` знанием сторон не является: пометив по
  // нему одни лишь врезки, мы получили бы чат, где роли есть у части
  // сегментов, `rolesKnown` стал бы `true` при нуле реплик оператора — и
  // правило «оператор не ответил» отсекло бы живой диалог, ровно как раньше.
  const sidesKnown = users != null && Object.keys(users).length > 0;
  if (senderId === '0' && sidesKnown) return 'system';

  if (!operatorId) return undefined;
  if (senderId === operatorId) return 'agent';
  if (senderId === '0') return 'system';
  return 'customer';
}

/** Почему чат не пошёл в анализ; `null` — пошёл. */
export type ChatInsignificanceReason = 'no_agent_reply' | 'too_few_messages';

export interface ChatSignificance {
  significant: boolean;
  reason: ChatInsignificanceReason | null;
  agentMessages: number;
  dialogueMessages: number;
}

/**
 * Стоит ли анализировать этот чат.
 *
 * Аналог Level 2 фильтра звонков (`minCallDurationSeconds`): у звонка мерилом
 * служит время разговора, у чата — реплики. Два правила:
 *
 * 1. Оператор ни разу не ответил — оценивать нечего, это не диалог, а
 *    оставленное сообщение. В проде таких 17.7 % чатов.
 * 2. Диалог короче порога проекта. Считаются только реплики `agent` и
 *    `customer`: `system` — служебные врезки платформы, они не разговор.
 *
 * Правило 1 не применяется, когда роль не проставлена НИ У ОДНОГО сегмента:
 * внешний источник мог их не прислать, и отсекать чат, про который мы не
 * знаем, кто говорил, значит терять живые диалоги. «Роль не проставлена»
 * проверяется по наличию поля (`role !== undefined`), а не по тому, встретился
 * ли `agent`/`customer` — чат из одних `system` (или `unknown`) сегментов
 * ролью размечен, просто в нём нет ни одной реплики оператора, и это ровно
 * правило 1, а не «роли неизвестны».
 */
export function evaluateChatSignificance(
  segments: ChatDiarizationSegment[],
  minChatMessages: number
): ChatSignificance {
  // Реплика без единого слова — не реплика. Сообщение, в котором был только
  // файл, доходит до транскрипта маркером (`selectOpenLineMessages`), и это
  // правильно: анализ должен знать, что клиент прислал фото. Но считать такой
  // маркер репликой значит пропустить в ПЛАТНЫЙ анализ переписку из одних
  // картинок — до маркеров она отсеивалась как «сообщений нет», а LLM всё
  // равно нечего в ней читать. Оценивается разговор, а не факт обмена файлами.
  //
  // То же для цитаты (#889): reply в Telegram приходит сообщением из одной
  // цитаты, а повтор оператора — его же прежней репликой в цитате. Ни то, ни
  // другое не новая реплика отправителя. Пересылка — другое дело: переслать
  // ответ коллеги и значит ответить, поэтому она реплику не отменяет.
  const spoken = segments.filter(
    (s) =>
      s.text
        .replace(QUOTE_REGION_ALL, (region) =>
          FORWARDED_QUOTE.test(region) ? region : ''
        )
        .replace(ATTACHMENT_MARKER_ALL, '')
        .trim().length > 0
  );
  const agentMessages = spoken.filter((s) => s.role === 'agent').length;
  const customerMessages = spoken.filter((s) => s.role === 'customer').length;
  const rolesKnown = segments.some((s) => s.role !== undefined);
  const dialogueMessages = rolesKnown ? agentMessages + customerMessages : spoken.length;

  if (rolesKnown && agentMessages === 0) {
    return {
      significant: false,
      reason: 'no_agent_reply',
      agentMessages,
      dialogueMessages,
    };
  }

  if (dialogueMessages < minChatMessages) {
    return {
      significant: false,
      reason: 'too_few_messages',
      agentMessages,
      dialogueMessages,
    };
  }

  return { significant: true, reason: null, agentMessages, dialogueMessages };
}

/**
 * Сообщение истории открытой линии, как его отдаёт `imopenlines.session.history.get`.
 *
 * Поля необязательны, потому что ответ REST ничего не гарантирует, и отбор это
 * уже знает: `text` приходит как `false` за пустое значение, `date` — тоже.
 * Форма жила тремя рукописными копиями (connectors, intake-консьюмер, шаг
 * ожидания), и они успели разойтись — в копии connectors не было `chatid`.
 */
export interface OpenLineHistoryMessage {
  id?: string;
  chatid?: string;
  senderid?: string;
  recipientid?: string;
  date?: string;
  text?: string;
  textlegacy?: string;
  params?: Record<string, unknown>;
}

/** Запись из словаря `files` того же ответа: тип нужен, имя — нет (это PII). */
export interface OpenLineHistoryFile {
  id?: string | number;
  type?: string;
  name?: string;
}

/**
 * Ответ `imopenlines.session.history.get`.
 *
 * Форма сообщения оставлена параметром: у каждого call-site свой уже
 * объявленный `Bitrix24ChatMessage`, и переписывать их — отдельная работа.
 * Общей здесь сделана оболочка, потому что именно её пришлось бы править
 * трижды: `files` добавлялся в три копии подряд, с одним и тем же
 * комментарием, — то самое расхождение в трёх местах, которое этот отбор и
 * съехался устранять.
 */
export interface OpenLineSessionHistory<M = OpenLineHistoryMessage> {
  /**
   * Ответ REST не типизирован, и поля может не быть — все три call-site уже
   * защищаются `!= null`, чтобы не записать в метаданные строку «undefined».
   * Тип обязан говорить то же самое, иначе tsc признаёт эти guard'ы мёртвыми.
   */
  chatId?: number;
  /**
   * НЕ пользователь. Портал кладёт сюда идентификатор чата (`"chat63350"`) —
   * проверено на живом ответе 2026-08-29. Поле объявлено, но не читается ни
   * одним call-site; имя провоцирует принять его за id клиента, чем оно не
   * является.
   */
  userId?: string;
  message?: Record<string, M>;
  /** Метаданные вложений, на которые ссылается `params.FILE_ID` сообщения. */
  files?: Record<string, OpenLineHistoryFile>;
  /**
   * Участники чата, ключ — id пользователя. Отсюда берётся СТОРОНА отправителя
   * (`resolveOpenLineMessageRole`): клиент помечен как заведённый коннектором.
   * Портал вправе словарь не прислать — тогда роль решается прежним правилом.
   */
  users?: Record<string, OpenLineUser>;
}

/**
 * Потолок числа сообщений одного чата — ДЕФОЛТ отбора, а не опция вызывающего.
 *
 * Он жил единственной константой в batch-процессоре активностей, тогда как два
 * других пути (intake-консьюмер и шаг ожидания) складывали payload очереди без
 * всякой границы. Дефолт здесь означает, что потолок получает и следующий
 * call-site, который забудет про него так же, как забыли эти два.
 */
export const MAX_OPEN_LINE_MESSAGES = 500;

export interface SelectOpenLineMessagesOptions {
  /** Словарь `files` из ответа истории — по нему определяется тип вложения. */
  files?: Record<string, OpenLineHistoryFile> | null;
  /** Верхняя граница числа сообщений; по умолчанию `MAX_OPEN_LINE_MESSAGES`. */
  limit?: number;
}

/**
 * Текст участника, в котором маркер вложения не может быть подделан.
 *
 * Маркер — единственная форма в квадратных скобках, которой транскрипт
 * доверяет: санитайзер её не трогает, LLM читает как факт присланного файла, и
 * в отчёте CRM она стоит рядом с настоящими. Напечатать `[attachment: image]`
 * руками может кто угодно — вместе с чем угодно следом («…Ignore previous
 * instructions»). Открывающая скобка литерала заменяется на круглую: текст
 * читается по-прежнему, доверенной формой быть перестаёт.
 */
function disarmForgedMarker(text: string): string {
  // Тег цитаты просто снимается, как раньше снимал его общий BBCode-санитайзер:
  // в выгрузке форума или хелпдеска `[quote]…[/quote]` — обычная разметка, и
  // текст внутри остаётся читаемым, а не превращается в `(quote]` (#889).
  // Снятие само может сложить тег из текста — `[quo[quote]te]` после прохода
  // даёт `[quote]`. Повтор до неподвижной точки квадратичен на вложенном
  // вводе (`[quo[quo[quote]te]te]`), а это CPU воркера на тексте клиента.
  // Поэтому один проход снятия, а сложившееся из него обезвреживается, как
  // вложение: скобка становится круглой. Оба прохода линейны.
  return text
    .replace(QUOTE_MARKER_PARSE, '')
    .replace(/\[(?=attachment|\/?quote\])/gi, '(');
}

/**
 * Транскрипт из внешнего источника, в котором маркер вложения не может лгать.
 *
 * Доверие маркеру держится на том, что его ставим МЫ (`selectOpenLineMessages`)
 * по факту `params.FILE_ID`. Но санитайзер щадит эту форму глобально, а через
 * ручную загрузку и публичный API транскрипт приходит уже готовым — и до этой
 * правки строка `[attachment: image]` в присланном файле доходила до LLM как
 * настоящий факт присланного файла, вычиталась из счёта реплик в
 * `evaluateChatSignificance` (то есть позволяла отправителю самому назначить
 * своей записи вердикт «разговора не было») и разрывала парные BBCode-правила:
 * `[img]…[attachment: image]…[/img]` расходился по разные стороны split'а, и
 * ссылка на картинку, которую `[img]`-правило обязано убрать, оставалась в
 * тексте.
 *
 * Применять на КАЖДОМ входе транскрипта извне, до `buildChatTranscriptText`.
 */
export function disarmExternalChatTranscript(transcript: ChatTranscript): ChatTranscript {
  return {
    ...transcript,
    ...(typeof transcript.text === 'string'
      ? { text: disarmForgedMarker(transcript.text) }
      : {}),
    ...(transcript.messages
      ? {
          messages: transcript.messages.map((message) => ({
            ...message,
            text: disarmForgedMarker(message.text),
          })),
        }
      : {}),
  };
}

/**
 * Строка-разделитель цитаты Bitrix: ровно 54 дефиса. Так выглядели все 79
 * цитат живого портала (#889); другие длины — обычные «divider lines» текста,
 * их снимает санитайзер.
 */
const QUOTE_DIVIDER = /^[ \t]*-{54}[ \t]*$/;
/**
 * Заголовок цитаты: `Имя [дата, время] #chatN/M`, `AuspexBot[18.09.2026 11:08:17]`,
 * `Имя [дата]`. Берётся только имя — дата вида «сьогодні, 10:47» со временем
 * теряет смысл, а `#chat…` — служебная ссылка.
 *
 * В имени нет скобок, а в скобках есть цифра (дата или время): иначе строка
 * пересланного `[b]Переслане повідомлення:[/B]` сошла бы за «имя [дата]».
 *
 * Классы идут без перекрытий, а цифра проверяется отдельно: прежняя форма
 * `[^\]]*\d[^\]]*` и ленивое имя перед `\s*` откатывались квадратично — строка
 * из 40 000 символов стоила секунду CPU на текст клиента. Длина строки
 * ограничена ещё и сверху: заголовок Bitrix — одна короткая строка.
 */
const QUOTE_HEADER = /^([^[\]]*)\[([^\]]*)\](?:\s*#chat\S*)?\s*$/;
const QUOTE_HEADER_MAX_LENGTH = 200;

/** Имя из заголовка цитаты Bitrix или `null`, если строка — не заголовок. */
function quoteHeaderAuthor(line: string): string | null {
  if (line.length > QUOTE_HEADER_MAX_LENGTH) return null;
  const match = QUOTE_HEADER.exec(line);
  return match && /\d/.test(match[2]!) ? match[1]!.trim() : null;
}
/**
 * Заголовок пересланного сообщения: `[b]Переслане повідомлення:[/B]`, без
 * имени. Остаётся в тексте цитаты. Только подписи самого Bitrix, а не любая
 * жирная строка с двоеточием: её может напечатать участник. На живом портале
 * замечена украинская; неузнанная подпись оставляет текст своим — так было и
 * до #889.
 */
const FORWARD_LABELS =
  'Переслане повідомлення|Пересланное сообщение|Forwarded message|Mensaje reenviado';
const FORWARD_HEADER = new RegExp(`^\\[b\\](?:${FORWARD_LABELS}):\\[\\/b\\]$`, 'i');
/** Цитата-пересылка — после санитайзера жирный тег может быть уже снят. */
const FORWARDED_QUOTE = new RegExp(
  `^\\[quote\\]\\s*(?:\\[b\\])?(?:${FORWARD_LABELS}):`,
  'i'
);

/**
 * Цитата Bitrix → наш маркер (#889).
 *
 * Bitrix кладёт цитату прямо в текст сообщения между строками из 54 дефисов, и
 * санитайзер снимал эти строки как «divider lines», а нормализация склеивала
 * всё в одну реплику: просьба клиента оказывалась словами оператора, слова
 * оператора из reply-цитаты — словами клиента.
 *
 * Вложенность Bitrix не балансирует: reply на цитату даёт четыре разделителя
 * подряд, и первая пара — один заголовок без текста. Только эта форма берётся
 * областью от ПЕРВОГО до ПОСЛЕДНЕГО разделителя; внутренние разделители
 * отбрасываются, внутренний заголовок сжимается до имени.
 *
 * Иначе цитаты идут парами разделителей с начала сообщения, у каждой заголовок
 * и текст; разбор останавливается на первой паре, которая цитатой не является.
 * Всё вне взятых пар — свой текст отправителя, включая лишний разделитель:
 * ответ после цитаты и строка из 54 дефисов под ним не уходят в цитату.
 *
 * Один разделитель — не цитата. Область без заголовка Bitrix первой непустой
 * строкой — тоже не цитата: такие строки может напечатать и сам участник, и
 * его слова не должны пропасть из счёта реплик.
 */
function markOpenLineQuote(text: string): string {
  const lines = text.replace(/\[br\]/gi, '\n').split(/\r?\n/);
  const dividers = lines.flatMap((line, index) =>
    QUOTE_DIVIDER.test(line) ? [index] : []
  );
  if (dividers.length < 2) return text;
  const own = (from: number, to: number) => lines.slice(from, to).join('\n').trim();
  const joined = (parts: string[]) => parts.filter((part) => part.length > 0).join('\n');

  const first = dividers[0]!;
  const opening = quoteParts(lines.slice(first + 1, dividers[1]!));
  const nested = dividers.length > 2 && opening !== null && opening.body.length === 0;
  if (nested) {
    const last = dividers[dividers.length - 1]!;
    const quote = quoteParts(lines.slice(first + 1, last))!;
    return joined([own(0, first), renderQuote(quote), own(last + 1, lines.length)]);
  }

  const parts: string[] = [];
  let cursor = 0;
  for (let i = 0; i + 1 < dividers.length; i += 2) {
    const [open, close] = [dividers[i]!, dividers[i + 1]!];
    const quote = quoteParts(lines.slice(open + 1, close));
    if (!quote || quote.body.length === 0) break;
    parts.push(own(cursor, open), renderQuote(quote));
    cursor = close + 1;
  }
  if (parts.length === 0) return text;
  parts.push(own(cursor, lines.length));
  return joined(parts);
}

/**
 * Автор и текст области цитаты, или `null`, если её первая непустая строка —
 * не заголовок Bitrix. Внутренние разделители отбрасываются; заголовок сразу
 * после внутреннего разделителя сжимается до `Имя:` — дата и `#chat…` не нужны.
 */
function quoteParts(region: string[]): { author: string; body: string } | null {
  const body: string[] = [];
  let author: string | null = null;
  let framed = false;
  let afterDivider = false;
  for (const line of region) {
    if (QUOTE_DIVIDER.test(line)) {
      afterDivider = true;
      continue;
    }
    const trimmed = line.trim();
    if (!framed) {
      if (!trimmed) continue;
      framed = true;
      author = quoteHeaderAuthor(trimmed);
      if (author === null && !FORWARD_HEADER.test(trimmed)) return null;
      if (author === null) body.push(line);
      continue;
    }
    if (afterDivider && trimmed) {
      afterDivider = false;
      const inner = quoteHeaderAuthor(trimmed);
      if (inner !== null) {
        if (inner) body.push(`${inner}:`);
        continue;
      }
    }
    body.push(line);
  }
  return framed ? { author: author ?? '', body: body.join('\n').trim() } : null;
}

function renderQuote(quote: { author: string; body: string }): string {
  return `${QUOTE_OPEN}${quote.author ? `${quote.author}: ` : ''}${quote.body}${QUOTE_CLOSE}`;
}

/** Идентификаторы файлов сообщения: Bitrix кладёт в `params.FILE_ID` либо массив, либо одно значение. */
function fileIdsOf(message: OpenLineHistoryMessage): string[] {
  const raw = message.params?.FILE_ID;
  if (raw === undefined || raw === null) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list
    .filter((value) => typeof value === 'string' || typeof value === 'number')
    .map((value) => String(value));
}

/**
 * Маркер вложения для транскрипта: `[attachment: image]`, `[attachment: image ×3]`.
 *
 * Тип берётся из словаря `files`; без него остаётся родовое `file` — знать, что
 * вложение БЫЛО, важнее, чем знать какое. Имя файла не подставляется намеренно:
 * оно уходит в LLM и в отчёт CRM, а «паспорт_Иванов.pdf» — это PII, которую
 * редактор PII в тексте сообщения не ждёт.
 */
function attachmentMarker(
  fileIds: string[],
  files: Record<string, OpenLineHistoryFile> | null | undefined
): string {
  const types = new Set(fileIds.map((id) => files?.[id]?.type || 'file'));
  const type = types.size === 1 ? types.values().next().value : 'file';
  return fileIds.length > 1
    ? `[attachment: ${type} ×${fileIds.length}]`
    : `[attachment: ${type}]`;
}

/**
 * Сообщения истории открытой линии, отобранные и упорядоченные для транскрипта.
 *
 * Три копии этого отбора жили в приёме чатов (intake-консьюмер, шаг ожидания
 * закрытия чата, batch-процессор активностей), и все три роняли часть диалога:
 *
 * - `m.text &&` выбрасывал сообщение БЕЗ подписи целиком, то есть присланное
 *   фото исчезало бесследно. Дальше по конвейеру `stripChatMarkup` снимает
 *   `[img]`/`[disk]`, а пустая строка отсеивается в `buildChatTranscriptText` —
 *   так вложение теряло последний след и на втором рубеже. Анализ судил
 *   оператора по неполному диалогу: на записи 1b8ee1b0 модель советовала
 *   «уточніть кольори», не зная, что клиент прислал фото товара первым же
 *   сообщением.
 * - `!m.text.includes('[URL=')` выбрасывал любое сообщение с размеченной
 *   ссылкой. Разметку и так снимает `stripChatMarkup`, а для интернет-магазина
 *   ссылка на товар — половина разговора.
 *
 * Что отбрасывается по-прежнему: служебные врезки платформы (`senderid === '0'`
 * и жирная шапка `[b]…`) и сообщение, в котором не осталось ни текста, ни
 * вложения. Регистр шапки НЕ игнорируется намеренно: кнопка «жирный» в Bitrix
 * даёт `[B]`, поэтому `[B]Доброго дня![/B] Мене звати Марина` — обычная реплика
 * оператора, и отбрасывать её значит терять единственный ответ оператора,
 * получая необратимое «оператор не ответил». Эвристика ненадёжна в обе стороны;
 * расширять её здесь — заводить новую потерю в правке против потерь.
 *
 * Нестроковые `text` и `date` читаются как отсутствующие, а не приводятся:
 * Bitrix отдаёт пустые скалярные поля как `false`. Для текста `String()` дал бы
 * реплику «false», а `?? ''` — `TypeError` на `.startsWith` (то есть retry и
 * INTAKE_DLQ). Для даты `new Date(false)` это вовсе не `Invalid Date`, а эпоха:
 * такое сообщение молча встало бы в начало разговора.
 */
export function selectOpenLineMessages<T extends OpenLineHistoryMessage>(
  messages: T[],
  options?: SelectOpenLineMessagesOptions
): Array<T & { text: string }> {
  return selectOpenLineMessagesDetailed(messages, options).messages;
}

/** Результат отбора вместе с тем, что по одному массиву восстановить нельзя. */
export interface OpenLineSelection<T> {
  messages: Array<T & { text: string }>;
  /** Сколько сообщений прошло отбор ДО применения потолка. */
  selectedCount: number;
  /** Потолок реально отрезал часть разговора. */
  truncated: boolean;
}

/**
 * То же, что `selectOpenLineMessages`, но с ответом на вопрос «а всё ли влезло».
 *
 * По одному итоговому массиву это не восстанавливается: разница между сырым
 * ответом и результатом складывается из ДВУХ причин — отсеянных служебных
 * врезок (норма) и среза по потолку (потеря куска разговора). Приём пытался
 * различить их эвристикой `rawCount > kept + 1`, и она ошибалась в обе стороны:
 * обычный чат с двумя служебными сообщениями поднимал ложную тревогу в проде, а
 * ровно одно сообщение, отрезанное потолком, не поднимало никакой.
 */
export function selectOpenLineMessagesDetailed<T extends OpenLineHistoryMessage>(
  messages: T[],
  options?: SelectOpenLineMessagesOptions
): OpenLineSelection<T> {
  const kept: Array<{ message: T; text: string; at: number }> = [];

  for (const message of messages) {
    const raw = typeof message.text === 'string' ? message.text : '';
    if (message.senderid === '0' || raw.startsWith('[b]')) continue;

    const text = markOpenLineQuote(disarmForgedMarker(raw)).trim();
    const fileIds = fileIdsOf(message);
    const marker = fileIds.length > 0 ? attachmentMarker(fileIds, options?.files) : '';
    const finalText = marker ? (text ? `${text} ${marker}` : marker) : text;
    if (finalText.length === 0) continue;

    // Время считается ОДИН раз на сообщение: сортировка иначе строила бы по паре
    // `Date` на сравнение, а это горячий путь воркера с бюджетом по CPU.
    // Сообщение без разбираемой даты идёт в начало — туда, откуда срез его и
    // выбросит первым, вместо того чтобы вытеснить настоящее.
    const at =
      typeof message.date === 'string' ? new Date(message.date).getTime() : Number.NaN;
    kept.push({ message, text: finalText, at: Number.isNaN(at) ? -Infinity : at });
  }

  kept.sort((a, b) => a.at - b.at);

  // Срез берёт ХВОСТ, а не голову: у разговора развязка в конце — согласие,
  // цена, договорённость о доставке. Прежний `slice(0, 500)` в batch-процессоре
  // при переполнении оставлял анализу одно вступление.
  const limit = options?.limit ?? MAX_OPEN_LINE_MESSAGES;
  const truncated = kept.length > limit;
  const window = truncated ? kept.slice(kept.length - limit) : kept;

  return {
    // Копия объекта строится ПОСЛЕ среза: до него это N клонов ради `limit` из них.
    messages: window.map((entry) => ({ ...entry.message, text: entry.text })),
    selectedCount: kept.length,
    truncated,
  };
}

/**
 * `media_meta` of a Bitrix24 open-line chat record: the CRM owner and the
 * chat's own CRM activity.
 *
 * A chat's `externalId` is the open-line SESSION id — the idempotency key, so it
 * stays — while the activity its report note is pinned to lives here. One
 * builder for every path that creates a chat record (webhook, CRM import) and
 * one reader for delivery, so the key cannot drift between them.
 */
export function buildChatMediaMeta(
  activityId: string,
  crm: { entityType: string; entityId: string } | null
): Record<string, unknown> {
  return { ...(crm ? { crm } : {}), crmActivityId: activityId };
}

/**
 * The chat's CRM activity id from `media_meta`, or null when none was stored
 * (chats ingested before it was) or the value is not a Bitrix24 id. A non-numeric
 * value degrades to "no note" instead of a write to a wrong activity.
 */
export function readChatActivityId(mediaMeta: string | null | undefined): string | null {
  if (!mediaMeta) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(mediaMeta);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const id = (parsed as { crmActivityId?: unknown }).crmActivityId;
  const text =
    typeof id === 'number' ? String(id) : typeof id === 'string' ? id.trim() : '';
  return /^[1-9]\d*$/.test(text) ? text : null;
}
